import { sortArrayBy } from '@domain-group/javascript-utils'

import { useModals, useSetState } from '@domain-group/react-hooks'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useProviderPriceListItems from '@hooks/useProviderPriceListItems'
import useProviderProductCodes from '@hooks/useProviderProductCodes'
import useSelector from '@hooks/useSelector'
import useServiceProducts from '@hooks/useServiceProducts'

import type {
  ProviderPriceListModel, ProviderPriceListItemModel, ProviderProductCodeModel,
} from '@models/types'

import type { EntitiesState } from '@redux/entities'

// @ts-expect-error TS7006
const modalCallbacks = (componentKey: string, setState) => {
  const componentCallbacks = {
    CreateOrEditProviderPriceListItemModal: {
      closeModal: () => setState({ showCreateOrEditProviderPriceListItemModal: false }),
      createProviderPriceListItem: async (payload: HandleCallbackActionParams) => {
        const { data, success } = await handleCallbackAction(payload)
        if (success && data){
          // @ts-expect-error TS7006
          setState({ selectedPriceListItemId: data?.id })
        }
      },
    },
    CreateOrEditProviderPriceListItemOptionModal: {
      closeModal: () => setState({ showCreateOrEditProviderPriceListItemOptionModal: false }),
      createProviderPriceListItemOption: async (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditProviderPriceListItemOptionValueModal: {
      closeModal: () => setState({ showCreateOrEditProviderPriceListItemOptionValueModal: false }),
      createProviderPriceListItemOptionValue: async (payload: HandleCallbackActionParams) => (
        handleCallbackAction(payload)
      ),
    },
  }

  // @ts-expect-error TS7053
  return componentCallbacks[componentKey] || {}
}

type CollectServiceProductIdsParams = {
  items: ProviderPriceListItemModel[] | ProviderProductCodeModel[],
}

const collectServiceProductIds = (params: CollectServiceProductIdsParams) => {
  const { items } = params

  const serviceProductIds = items.map(item => item.service_product_id)
  const uniqueProductIds = Array.from(new Set(serviceProductIds))

  return uniqueProductIds
}

type FindAndSortServiceProductIdsParams = {
  filteredProviderPriceListItems: ProviderPriceListItemModel[],
  serviceProducts: EntitiesState['serviceProducts'],
}

const findAndSortServiceProductIds = (params: FindAndSortServiceProductIdsParams) => {
  const { filteredProviderPriceListItems, serviceProducts } = params

  const visibleItems = filteredProviderPriceListItems.filter(item => !item.hidden)
  const hiddenItems = filteredProviderPriceListItems.filter(item => item.hidden)

  const productIdsFromVisibleItems = collectServiceProductIds({ items: visibleItems })
  const productIdsFromHiddenItems = collectServiceProductIds({ items: hiddenItems })

  const sortedVisibleProductIds = sortArrayBy(productIdsFromVisibleItems, 'asc', id => serviceProducts[id]?.title)
  const sortedHiddenProductIds = sortArrayBy(productIdsFromHiddenItems, 'asc', id => serviceProducts[id]?.title)

  // Ensure we are not receiving duplicate product IDs and maintain the separate sorts of visible and hidden
  const assignedServiceProductIds = Array.from(new Set(sortedVisibleProductIds.concat(sortedHiddenProductIds)))

  return assignedServiceProductIds
}

type DefaultState = {
  selectedPriceListItemId: number | undefined,
  showCreateOrEditProviderPriceListItemModal: boolean,
  showCreateOrEditProviderPriceListItemOptionModal: boolean,
  showCreateOrEditProviderPriceListItemOptionValueModal: boolean,
}

const defaultState: DefaultState = {
  selectedPriceListItemId: undefined,
  showCreateOrEditProviderPriceListItemModal: false,
  showCreateOrEditProviderPriceListItemOptionModal: false,
  showCreateOrEditProviderPriceListItemOptionValueModal: false,
}

type UsePriceListManagerOptions = {
  providerPriceList: ProviderPriceListModel,
}

function usePriceListManager(options: UsePriceListManagerOptions) {
  const { providerPriceList } = options

  const [state, setState] = useSetState<DefaultState>(defaultState)
  const { selectedPriceListItemId } = state

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const { providerPriceListItems, serviceProducts } = useSelector(redux => redux.entities)
  // @ts-expect-error TS2538
  const selectedPriceListItem = providerPriceListItems[selectedPriceListItemId] || {}

  // Product Codes (Assigned Products)
  const providerProductCodesPayload = useProviderProductCodes({
    filters: {
      provider_id: providerPriceList.provider_id,
      service_id: providerPriceList.service_id,
    },
    performHttpRequests: !!providerPriceList.provider_id,
  })

  const { filteredProviderProductCodes } = providerProductCodesPayload

  // Price List Items
  const providerPriceListItemsPayload = useProviderPriceListItems({
    filters: {
      provider_price_list_id: providerPriceList.id,
    },
    performHttpRequests: !!providerPriceList.id,
  })

  const { filteredProviderPriceListItems } = providerPriceListItemsPayload

  const assignedServiceProductIds = findAndSortServiceProductIds({ filteredProviderPriceListItems, serviceProducts })

  const permittedServiceProductIds = collectServiceProductIds({ items: filteredProviderProductCodes })

  const unassignedServiceProductIds = permittedServiceProductIds.filter(productId => (
    !assignedServiceProductIds.includes(productId)))

  const mergedServiceProductIds = Array.from(new Set([...assignedServiceProductIds, ...permittedServiceProductIds]))

  // Service Products
  const serviceProductsPayload = useServiceProducts({
    filters: {
      service_product_ids: mergedServiceProductIds,
    },
    performHttpRequests: !!assignedServiceProductIds.length && !!permittedServiceProductIds.length,
  })

  return {
    assignedServiceProductIds,
    callbacks: {
      modalCallbacks: (modalKey: string) => modalCallbacks(modalKey, setState),
      selectPriceListItemId: (priceListItemId: number | undefined) => {
        setState({ selectedPriceListItemId: priceListItemId })
      },
      showCreateOrEditProviderPriceListItemModal: (payload: object) => {
        setModalData('CreateOrEditProviderPriceListItemModal', payload)
        setState({ showCreateOrEditProviderPriceListItemModal: true })
      },
      showCreateOrEditProviderPriceListItemOptionModal: (payload: object) => {
        setModalData('CreateOrEditProviderPriceListItemOptionModal', payload)
        setState({ showCreateOrEditProviderPriceListItemOptionModal: true })
      },
      showCreateOrEditProviderPriceListItemOptionValueModal: (payload: object) => {
        setModalData('CreateOrEditProviderPriceListItemOptionValueModal', payload)
        setState({ showCreateOrEditProviderPriceListItemOptionValueModal: true })
      },
    },
    isMaster: providerPriceList.is_master,
    modalContext,
    providerPriceList,
    providerPriceListItemsPayload,
    providerProductCodesPayload,
    selectedPriceListItem,
    serviceProductsPayload,
    state,
    unassignedServiceProductIds,
  }
}

export default usePriceListManager
